/* eslint-disable */
// /src/services/franchiseStoreOrderService.js
import {
    collection,
    doc,
    addDoc,
    getDoc,
    getDocs,
    query,
    where,
    orderBy,
    limit as qlimit,
    serverTimestamp,
    updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";

// 가맹점 스토어 주문 컬렉션
const COL = "franchise_store_orders";
const COL_REF = collection(db, COL);

// 숫자만 남기는 전화번호 정규화
const normalizePhone = (v = "") => String(v).replace(/[^\d]/g, "");

// 숫자 정규화
const toInt = (v) => {
    if (v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

function safeInt(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.trunc(n);
}

/**
 * 가맹점 주문 생성 (멀티 아이템 지원)
 *
 * 추가(포인트):
 *   pointsUsed?: number
 *   payableTotal?: number
 *
 * - price.total: 원래 총액(상품+배송)
 * - price.payableTotal: 포인트 적용 후 실제 결제/입금 금액
 * - points.used: 사용 포인트
 * - payment.method: bank_transfer
 * - payment.pointsUsed: 사용 포인트
 */
export async function createFranchiseStoreOrder(input) {
    if (!input) {
        throw new Error("createFranchiseStoreOrder: payload is required");
    }

    const ownerPhoneE164 = input.ownerPhoneE164 || "";
    if (!ownerPhoneE164) {
        throw new Error("createFranchiseStoreOrder: ownerPhoneE164 is required");
    }

    const channel = input.channel || "franchise";
    const ordererRaw = input.orderer || {};
    const receiverRaw = input.receiver || {};
    const status = input.status || "pending_deposit";

    let items = [];

    // ✅ 1) 멀티 아이템 시그니처 (items 배열)
    if (Array.isArray(input.items) && input.items.length > 0) {
        items = input.items.map((raw) => {
            const optionSelections = Array.isArray(raw.optionSelections)
                ? raw.optionSelections.map((op) => ({
                    groupName: op.groupName || "",
                    value: op.value || "",
                    extraPrice: toInt(op.extraPrice),
                }))
                : [];

            const basePrice = toInt(raw.unitPrice);
            const extraTotal = optionSelections.reduce((sum, op) => sum + toInt(op.extraPrice), 0);
            const qty = toInt(raw.qty || 1);
            const goodsSubtotal = (basePrice + extraTotal) * qty;
            const shippingFee = toInt(raw.shippingFeePerItem || raw.shippingFee || 0);

            return {
                productId: raw.productId || "",
                productTitle: raw.productTitle || raw.productTitleSnapshot || "",
                thumb: raw.thumb || "",
                unitPrice: basePrice,
                qty,
                optionSelections,
                goodsSubtotal,
                shippingFee,
            };
        });
    } else {
        // ✅ 2) 구버전 단일 아이템 시그니처
        const optionSelections = Array.isArray(input.optionSelections)
            ? input.optionSelections.map((op) => ({
                groupName: op.groupName || "",
                value: op.value || "",
                extraPrice: toInt(op.extraPrice),
            }))
            : [];

        const basePrice = toInt(input.unitPrice);
        const extraTotal = optionSelections.reduce((sum, op) => sum + toInt(op.extraPrice), 0);
        const qty = toInt(input.qty || 1);
        const goodsSubtotal = (basePrice + extraTotal) * qty;
        const shippingFee = toInt(input.shippingFee || 0);

        items = [
            {
                productId: input.productId || "",
                productTitle: input.productTitle || input.productTitleSnapshot || "",
                thumb: input.thumb || "",
                unitPrice: basePrice,
                qty,
                optionSelections,
                goodsSubtotal,
                shippingFee,
            },
        ];
    }

    if (!items.length) {
        throw new Error("createFranchiseStoreOrder: items is empty");
    }

    // ✅ 금액 집계
    const itemsTotal = items.reduce((sum, it) => sum + toInt(it.goodsSubtotal), 0);

    let shippingTotal;
    if (typeof input.shippingTotal === "number") {
        shippingTotal = toInt(input.shippingTotal);
    } else {
        shippingTotal = items.reduce((sum, it) => sum + toInt(it.shippingFee), 0);
    }

    const total = itemsTotal + shippingTotal;

    // ======================================================
    // ✅ 포인트/실결제: 어디로 오든 읽어오게 방어 처리
    //  - pointsUsed: input.pointsUsed -> input.pricing.pointsUsed -> input.payment.pointsUsed -> 0
    //  - payableTotal: input.payableTotal -> input.pricing.payablePrice/payableTotal -> 계산
    // ======================================================
    const pointsUsedRaw =
        input?.pointsUsed ??
        input?.pricing?.pointsUsed ??
        input?.payment?.pointsUsed ??
        0;

    const pointsUsed = Math.max(0, safeInt(pointsUsedRaw));

    const payableRaw =
        input?.payableTotal ??
        input?.pricing?.payablePrice ??
        input?.pricing?.payableTotal ??
        null;

    const payableTotal =
        typeof payableRaw === "number"
            ? Math.max(0, safeInt(payableRaw))
            : Math.max(0, total - pointsUsed);

    // ✅ 주문자/수령자 정제 (전화번호 정규화 포함)
    const ordererPhoneNorm = normalizePhone(ordererRaw.phone);
    const receiverPhoneNorm = normalizePhone(receiverRaw.phone);

    const orderer = {
        name: ordererRaw.name || "",
        phone: ordererPhoneNorm || "",
        email: ordererRaw.email || "",
        zip: ordererRaw.zip || "",
        addr1: ordererRaw.addr1 || "",
        addr2: ordererRaw.addr2 || "",
    };

    const receiver = {
        name: receiverRaw.name || "",
        phone: receiverPhoneNorm || "",
        zip: receiverRaw.zip || "",
        addr1: receiverRaw.addr1 || "",
        addr2: receiverRaw.addr2 || "",
        memo: receiverRaw.memo || "",
    };

    const now = serverTimestamp();

    const first = items[0];
    const productTitleSnapshot =
        items.length > 1
            ? `${first.productTitle || ""} 외 ${items.length - 1}건`
            : first.productTitle || "";

    const docData = {
        ownerPhoneE164,
        channel,
        items,

        price: {
            itemsTotal,
            shippingTotal,
            total,
            payableTotal,
        },

        points: {
            used: pointsUsed,
        },

        payment: {
            method: "bank_transfer",
            pointsUsed: pointsUsed,
            // ✅ 화면/디버깅에 도움되면 같이 보관
            payableTotal: payableTotal,
        },

        // ✅ (테스트/화면 편의) 상위 요약 필드도 함께
        pointsUsed: pointsUsed,
        payableTotal: payableTotal,

        orderer,
        receiver,
        status,
        createdAt: now,
        updatedAt: now,

        // ✅ 기존 화면/관리자 호환용 요약 필드
        productId: first.productId || "",
        productTitleSnapshot,
        unitPrice: first.unitPrice,
        shippingFee: shippingTotal,
        qty: first.qty,
        totalPrice: total,
    };

    const ref = await addDoc(COL_REF, docData);
    return ref.id;
}


/**
 * 단건 조회
 */
export async function getFranchiseStoreOrder(orderId) {
    const snap = await getDoc(doc(db, COL, orderId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * 주문자 휴대폰 기준 목록 조회 (정보성)
 */
export async function listFranchiseStoreOrdersByPhone(phone, { limitCount = 50 } = {}) {
    const norm = normalizePhone(phone);
    if (!norm) return [];

    try {
        console.log("[franchiseOrders] listByPhone start", norm, "limit=", limitCount);
        const q = query(COL_REF, where("orderer.phone", "==", norm), orderBy("createdAt", "desc"), qlimit(limitCount));
        const snaps = await getDocs(q);
        const rows = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        console.log("[franchiseOrders] listByPhone OK rows=", rows.length);
        return rows;
    } catch (e) {
        console.error("[franchiseOrders] listFranchiseStoreOrdersByPhone error =", e);
        throw e;
    }
}

/**
 * ownerPhoneE164 기준 주문 목록 조회
 * - 드라이버 로그인 기준 "내 가맹점 주문 내역"용
 */
export async function listFranchiseStoreOrdersByOwnerPhoneE164(ownerPhoneE164, { limitCount = 50 } = {}) {
    if (!ownerPhoneE164) return [];
    try {
        console.log("[franchiseOrders] listByOwner start ownerPhoneE164=", ownerPhoneE164, "limit=", limitCount);
        const q = query(COL_REF, where("ownerPhoneE164", "==", ownerPhoneE164), orderBy("createdAt", "desc"), qlimit(limitCount));
        const snaps = await getDocs(q);
        const rows = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        console.log("[franchiseOrders] listByOwner OK rows=", rows.length);
        return rows;
    } catch (e) {
        console.error("[franchiseOrders] listFranchiseStoreOrdersByOwnerPhoneE164 error =", e);
        throw e;
    }
}

/**
 * 관리자용: 전체 주문 목록 조회
 */
export async function listFranchiseStoreOrdersForAdmin({ limitCount = 300 } = {}) {
    try {
        const q = query(COL_REF, orderBy("createdAt", "desc"), qlimit(limitCount));
        const snaps = await getDocs(q);
        const rows = snaps.docs.map((d) => ({ id: d.id, ...d.data() }));
        return rows;
    } catch (e) {
        console.error("[franchiseOrders] listFranchiseStoreOrdersForAdmin error =", e);
        throw e;
    }
}

/**
 * 상태 변경 (관리자/백오피스용)
 */
export async function updateFranchiseStoreOrderStatus(orderId, status) {
    const ref = doc(db, COL, orderId);
    await updateDoc(ref, {
        status,
        updatedAt: serverTimestamp(),
    });
    return true;
}

/**
 * 주문 문서 patch 업데이트 (관리자 상세 수정용)
 */
export async function updateFranchiseStoreOrder(orderId, patch) {
    if (!orderId) throw new Error("updateFranchiseStoreOrder: orderId is required");
    if (!patch || typeof patch !== "object") return;

    const ref = doc(db, COL, orderId);
    await updateDoc(ref, {
        ...patch,
        updatedAt: serverTimestamp(),
    });
}
